import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, CornerDownLeft, Package, Receipt, Zap } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import { dinero } from '@/lib/formato-tienda';
import { cn } from '@/lib/cn';

/**
 * PALETA DE COMANDOS — ⌘K / Ctrl+K.
 *
 * Reemplaza el buscador que flotaba en su propia fila arriba de cada pantalla,
 * ocupando alto sin dar nada. Ahora vive en el header y hace **dos** cosas:
 *
 *  1. **Buscar** productos y ventas.
 *  2. **Ejecutar**: «Nueva venta», «Registrar entrada», «Agregar gasto», «Ir a
 *     Estancados».
 *
 * ═══ POR QUÉ LAS ACCIONES SALEN PRIMERO CON LA CAJA VACÍA ═══
 *
 * Abrir la paleta sin escribir nada casi siempre significa «quiero hacer algo»,
 * no «quiero buscar algo»: para buscar ya se escribe directo. Con la caja vacía
 * se ofrecen las cuatro acciones del día; al escribir, manda lo encontrado.
 *
 * ═══ EL RETARDO ═══
 *
 * Los productos se piden al servidor, así que el texto espera 200 ms. Sin eso,
 * escribir «vestido» manda siete peticiones y la última en llegar puede no ser
 * la última que se escribió.
 */

interface Comando {
  id: string;
  etiqueta: string;
  detalle?: string;
  grupo: 'Acciones' | 'Productos' | 'Ventas';
  icono: typeof Package;
  ejecutar: () => void;
}

export function PaletaDeComandos() {
  const [abierta, setAbierta] = useState(false);
  const [texto, setTexto] = useState('');
  const [consulta, setConsulta] = useState('');
  const [cursor, setCursor] = useState(0);
  const navegar = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K desde cualquier parte, y Esc para cerrar.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAbierta((v) => !v);
      }
      if (e.key === 'Escape') setAbierta(false);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, []);

  useEffect(() => {
    if (abierta) setTimeout(() => inputRef.current?.focus(), 10);
    else {
      setTexto('');
      setConsulta('');
      setCursor(0);
    }
  }, [abierta]);

  useEffect(() => {
    const t = setTimeout(() => setConsulta(texto.trim()), 200);
    return () => clearTimeout(t);
  }, [texto]);

  const { data: productos } = useQuery({
    queryKey: ['paleta', 'productos', consulta],
    queryFn: () => tienda.listarProductos({ buscar: consulta, limit: 6 }),
    enabled: abierta && consulta.length >= 2,
  });

  const { data: ventas } = useQuery({
    queryKey: ['paleta', 'ventas'],
    queryFn: () => tienda.listarVentas({ limit: 50 }),
    enabled: abierta,
  });

  const comandos = useMemo<Comando[]>(() => {
    const ir = (ruta: string) => () => {
      setAbierta(false);
      navegar(ruta);
    };

    const acciones: Comando[] = [
      {
        id: 'a-venta',
        etiqueta: 'Nueva venta',
        detalle: 'Arma una factura',
        grupo: 'Acciones',
        icono: Zap,
        ejecutar: ir('/ventas?nueva=1'),
      },
      {
        id: 'a-entrada',
        etiqueta: 'Registrar entrada de mercancía',
        detalle: 'Suma piezas al inventario',
        grupo: 'Acciones',
        icono: Zap,
        ejecutar: ir('/inventario?vista=por-acabarse'),
      },
      {
        id: 'a-gasto',
        etiqueta: 'Agregar gasto',
        grupo: 'Acciones',
        icono: Zap,
        ejecutar: ir('/finanzas?gasto=nuevo'),
      },
      {
        id: 'a-estancados',
        etiqueta: 'Ir a Estancados',
        detalle: 'Lo que no se mueve',
        grupo: 'Acciones',
        icono: Zap,
        ejecutar: ir('/inventario?vista=estancados'),
      },
      {
        id: 'a-cargar',
        etiqueta: 'Cargar inventario de un golpe',
        grupo: 'Acciones',
        icono: Zap,
        ejecutar: ir('/productos/importar'),
      },
    ];

    const q = consulta.toLowerCase();
    const accionesFiltradas = q
      ? acciones.filter((a) => a.etiqueta.toLowerCase().includes(q))
      : acciones;

    const deProductos: Comando[] = (productos ?? []).map((p) => ({
      id: `p-${p.id}`,
      etiqueta: p.name,
      detalle: `${p.sku ?? 'sin código'} · ${dinero(p.price)}`,
      grupo: 'Productos',
      icono: Package,
      ejecutar: ir(`/productos?buscar=${encodeURIComponent(p.sku ?? p.name)}`),
    }));

    const deVentas: Comando[] = q
      ? (ventas ?? [])
          .filter((v) => v.numero.toLowerCase().includes(q))
          .slice(0, 5)
          .map((v) => ({
            id: `v-${v.id}`,
            etiqueta: v.numero,
            detalle: v.estado.toLowerCase(),
            grupo: 'Ventas' as const,
            icono: Receipt,
            ejecutar: ir(`/ventas?venta=${v.id}`),
          }))
      : [];

    // Con texto manda lo encontrado; sin texto, lo que se puede hacer.
    return q ? [...deProductos, ...deVentas, ...accionesFiltradas] : accionesFiltradas;
  }, [consulta, productos, ventas, navegar]);

  useEffect(() => setCursor(0), [comandos.length]);

  if (!abierta) {
    return (
      <button
        type="button"
        onClick={() => setAbierta(true)}
        title="Buscar o hacer algo (⌘K)"
        className={cn(
          // 21-oct, macOS 27: cápsula de cristal de la barra de arriba.
          'mac-capsula px-3.5 text-sm text-slate-500 dark:text-slate-400',
        )}
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Buscar o hacer algo</span>
        <kbd className="hidden rounded-[4px] bg-[var(--mac-btn)] px-1 font-sans text-[10px] sm:inline">⌘K</kbd>
      </button>
    );
  }

  const porGrupo = comandos.reduce<Record<string, Comando[]>>((acc, c) => {
    (acc[c.grupo] ??= []).push(c);
    return acc;
  }, {});
  let indice = -1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={() => setAbierta(false)}
        className="mac-velo absolute inset-0"
      />
      <div className="mac-hoja relative w-full max-w-xl overflow-hidden rounded-xl">
        <div className="flex items-center gap-2 border-b border-surface-border px-4">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, comandos.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                comandos[cursor]?.ejecutar();
              }
            }}
            placeholder="Busca un producto o una venta, o escribe lo que quieres hacer…"
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
          <kbd className="rounded-[4px] bg-[var(--mac-btn)] px-1 font-sans text-[10px] text-slate-500">esc</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto py-1">
          {comandos.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">
              Nada con «{consulta}». Prueba con el código de la pieza.
            </p>
          ) : (
            Object.entries(porGrupo).map(([grupo, items]) => (
              <div key={grupo}>
                <p className="px-4 pb-1 pt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                  {grupo}
                </p>
                {items.map((c) => {
                  indice += 1;
                  const activo = indice === cursor;
                  const Icono = c.icono;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onMouseEnter={() => setCursor(comandos.indexOf(c))}
                      onClick={c.ejecutar}
                      className={cn(
                        // Fila de menú de macOS: la del cursor, llena del color del negocio.
                        'mx-1.5 flex w-[calc(100%-0.75rem)] items-center gap-3 rounded-[7px] px-2.5 py-1.5 text-left text-sm',
                        activo ? 'bg-acento-lleno text-white [&_*]:!text-white' : 'text-slate-900 dark:text-slate-100',
                      )}
                    >
                      <Icono className="h-4 w-4 shrink-0 text-acento-icono" />
                      <span className="min-w-0 flex-1 truncate">
                        {c.etiqueta}
                      </span>
                      {c.detalle && (
                        <span className="shrink-0 text-xs text-slate-400">{c.detalle}</span>
                      )}
                      {activo && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
